import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellOff, X, Check } from "lucide-react";
import { isPushSupported, getCurrentSubscription, subscribePush, unsubscribePush } from "@/lib/push";

const DISMISS_KEY = "sbc-push-dismissed";
const DISMISS_DAYS = 30;

const TOPICS = [
  { id: "launches", label: "New product launches" },
  { id: "promotions", label: "Promotions & offers" },
  { id: "distributor", label: "Distributor announcements" },
  { id: "news", label: "Company news" },
];

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - parseInt(raw, 10) < DISMISS_DAYS * 86400 * 1000;
  } catch { return false; }
}

export default function PushSubscribeCard() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [selected, setSelected] = useState(TOPICS.map(t => t.id));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isPushSupported()) return;
    setSupported(true);
    getCurrentSubscription().then((s) => {
      setSubscribed(Boolean(s));
      if (!s && !isDismissed()) {
        const t = setTimeout(() => setOpen(true), 6000);
        return () => clearTimeout(t);
      }
    });
  }, []);

  if (!supported) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setOpen(false);
  };

  const doSubscribe = async () => {
    setPending(true); setError("");
    try {
      await subscribePush(selected);
      setSubscribed(true);
      setOpen(false);
    } catch (e) {
      setError(e.message === "Permission denied"
        ? "Notifications are blocked in your browser settings."
        : "Could not enable notifications right now.");
    } finally { setPending(false); }
  };

  const doUnsubscribe = async () => {
    setPending(true);
    try {
      await unsubscribePush();
      setSubscribed(false);
    } finally { setPending(false); }
  };

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(t => t !== id) : [...s, id]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 220, damping: 24 }}
          className="fixed z-[75] bottom-4 left-4 sm:left-6 max-w-sm rounded-2xl bg-neutral-950 text-white border border-white/10 shadow-2xl overflow-hidden"
          data-testid="push-subscribe-card"
        >
          <div className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 grid place-items-center text-[#D4AF37]">
                  <Bell className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold">Stay updated</div>
                  <h3 className="text-base font-bold tracking-tight leading-tight">Get SBC updates on this device</h3>
                </div>
              </div>
              <button onClick={dismiss} className="text-white/60 hover:text-white" aria-label="Dismiss" data-testid="push-dismiss">
                <X className="w-4 h-4" />
              </button>
            </div>

            {!subscribed ? (
              <>
                <p className="mt-3 text-sm text-neutral-300">Choose what you&apos;d like to hear about:</p>
                <div className="mt-3 space-y-1.5">
                  {TOPICS.map(t => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:text-[#D4AF37] transition-colors">
                      <span
                        className={`w-4 h-4 rounded border ${selected.includes(t.id) ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/30"} grid place-items-center`}
                        onClick={() => toggle(t.id)}
                      >
                        {selected.includes(t.id) && <Check className="w-3 h-3 text-neutral-950" strokeWidth={3} />}
                      </span>
                      <span onClick={() => toggle(t.id)}>{t.label}</span>
                    </label>
                  ))}
                </div>
                {error && <p className="mt-3 text-xs text-red-400">{error}</p>}
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={doSubscribe} disabled={pending || selected.length === 0}
                    className="flex-1 bg-[#D4AF37] hover:bg-amber-400 disabled:opacity-50 text-neutral-950 rounded-full py-2.5 text-xs font-bold uppercase tracking-widest transition"
                    data-testid="push-enable-btn"
                  >
                    {pending ? "Enabling…" : "Enable"}
                  </button>
                  <button onClick={dismiss} className="flex-1 bg-white/5 hover:bg-white/10 rounded-full py-2.5 text-xs font-semibold">
                    Not now
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-3 text-sm text-neutral-300">You&apos;re subscribed to SBC updates on this device.</p>
                <div className="mt-4">
                  <button
                    onClick={doUnsubscribe} disabled={pending}
                    className="w-full inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 rounded-full py-2.5 text-xs font-semibold"
                    data-testid="push-disable-btn"
                  >
                    <BellOff className="w-3.5 h-3.5" /> {pending ? "Disabling…" : "Turn off notifications"}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
